import * as React from 'react'
import { AlertCircle } from 'lucide-react'
import { Label } from './label'
import { Input } from './input'

interface InputUSDProps {
  id?: string
  label?: string
  value?: string | number
  onChange?: (value: number) => void
  placeholder?: string
  min?: number
  max?: number
  decimalScale?: number
  required?: boolean
  error?: string
  className?: string
  labelClassName?: string
}

export function InputUSD({
  id,
  label,
  value,
  onChange,
  placeholder = '0.00',
  min = Number.NEGATIVE_INFINITY,
  max = Number.POSITIVE_INFINITY,
  decimalScale = 8,
  required,
  error,
  className,
  labelClassName,
}: InputUSDProps) {
  const generatedId = React.useId()
  const inputId = id || `input-usd-${generatedId}`

  const [display, setDisplay] = React.useState<string>('')
  const [focused, setFocused] = React.useState(false)

  const formatCurrency = React.useCallback(
    (num: number): string => {
      if (!isFinite(num)) return ''
      return new Intl.NumberFormat('en-US', {
        style: 'decimal',
        maximumFractionDigits: decimalScale,
        minimumFractionDigits: 0,
      }).format(num)
    },
    [decimalScale],
  )

  const toNumber = (v: unknown): number | undefined => {
    if (v === undefined || v === null || v === '') return undefined
    if (typeof v === 'number') return v
    const parsed = Number.parseFloat(String(v))
    return isNaN(parsed) ? undefined : parsed
  }

  React.useEffect(() => {
    const current = toNumber(value)
    if (!focused) {
      setDisplay(current === undefined ? '' : formatCurrency(current))
    }
  }, [value, focused, formatCurrency])

  const handleFocus = () => {
    setFocused(true)
    setDisplay((prev) => prev.replace(/[^0-9.]/g, ''))
  }

  const handleBlur = () => {
    setFocused(false)
    const n = Number.parseFloat(display || '0')
    if (isNaN(n)) {
      setDisplay('')
      onChange?.(0)
      return
    }
    const clamped = Math.min(Math.max(n, min ?? n), max ?? n)
    onChange?.(clamped)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let v = e.target.value
    if (decimalScale === 0) {
      if (/^\d*$/.test(v) || v === '') {
        setDisplay(v)
      }
    } else {
      if (/^\d*\.?\d*$/.test(v) || v === '') {
        const parts = v.split('.')
        if (parts[1] && parts[1].length > decimalScale) {
          v = parts[0] + '.' + parts[1].slice(0, decimalScale)
        }
        setDisplay(v)
      }
    }
  }

  return (
    <div className={className}>
      {label && (
        <Label htmlFor={inputId} className={labelClassName}>
          {label}
        </Label>
      )}
      <div className="relative">
        <Input
          id={inputId}
          type="text"
          value={display}
          onChange={handleChange}
          onKeyDown={(e) => {
            if (
              /[0-9]/.test(e.key) ||
              e.key === '.' ||
              e.key === 'Backspace' ||
              e.key === 'Delete' ||
              e.key === 'ArrowLeft' ||
              e.key === 'ArrowRight' ||
              e.key === 'Tab'
            ) {
              return
            }
            e.preventDefault()
          }}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          className={error ? 'border-red-500 pr-12' : 'pr-12'}
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          <span className="text-sm text-neutral-500">USD</span>
        </div>
      </div>
      {error && (
        <p className="text-xs text-status-error mt-1 inline-flex items-center gap-1">
          <AlertCircle className="w-3 h-3" />
          {error}
        </p>
      )}
    </div>
  )
}
